import {IExcelDataAbstractState} from '@shared/components/excel';
import {IDisplayColumn} from '@shared/models/idb';
import {IResponseApi} from '@core/models';


/**
 * Param name chứa auth code dùng để cấp quyền Google Sheet
 * */
export const GOOGLE_SHEET_AUTH_CODE_PARAM = 'ggAuthCode';
export const GOOGLE_SHEET_OPEN_MODAL_EXPORT = 'isOpenExportGG';
export const GOOGLE_SHEET_OPEN_MODAL_IMPORT = 'isOpenImportGG';

export const GOOGLE_SHEET_ACCESS_TOKEN_CONSTS = {
  TYPE_SCOPE_USER: 1, // Token sử dụng dạng "Cá nhân"
  TYPE_SCOPE_BUSINESS: 2, // Token sử dụng dạng "Doanh nghiệp" có thể cho các user trong DN dùng

  // Khai báo các kiểu sheet logic hỗ trợ khi export data
  SHEET_TYPE_USE_ALREADY_EXISTS: 1, // Chọn sheetName đã có sẵn trong file
  SHEET_TYPE_USE_CREATE_NEW: 2, // Tạo mới file theo cấu trúc quy định
  SHEET_TYPE_USE_CREATE_RANGE: 3, // Tạo theo logic của từng màn VD: đơn hàng theo ngày tạo đơn
}

export type IBusinessExportDataSetting = {
  id: number,
  businessId: number,
  userId: number,
  type: number,
  scopeType: number,
  format: number,
  name: string,
  columns: IDisplayColumn[];
  configs: {
    typeUseFile: number,
    pageSelection: IPageSelectionExportData,
    fileNew: {
      fileName: string,
      sheetName: string,
    },
    // Dùng riêng "Google Sheet"
    googleFile: {
      typeUseSheet: number, // Loại sheet đang dùng
      spreadUrl: string,
      spreadSheetNewTitle: string,
      // 2 thuộc tính lưu chọn giá trị từ sheet đã có sẵn
      spreadSheetId: number,
      spreadSheetTitle: string,
    },
  },
  createdAt: string | null,
  updatedAt: string | null,
}
export type IGoogleSheetAccessToken = {
  id: number,
  userId: number,
  name: string,
  email: string,
  createdAt: string | null,
  updatedAt: string | null,
}


/** ---------------------------------------------------------------------------------------------------------------------
 * Common
 * ---------------------------------------------------------------------------------------------------------------------
 * */
type IDataExportSpreadData = {
  spreadId: string,
  spreadUrl: string,
  spreadSheetId: number,
  spreadSheetTitle: string,
}

export interface IResExportDataGGS<T = any[]> {
  totalPages: number,
  totalItems: number,
  page: number,
  result: T
  spreadData: IDataExportSpreadData,
  next?: any[],
  isFinishPages?: boolean,
  logExportId: string,
  logIndexId: string,
  lastExportId: string,
}

export type IExportDataGGSPaginatorAPI = {
  res: IResponseApi<IResExportDataGGS>;
  isLastPage: boolean
}

export type TPaginatorExportDataGoogleSheet = {
  params: any,
  service: any,
  func: string,
  outputSetting: IConfigExportDataGoogleSheet,
  excelData?: any[], // Dữ liệu export
  excelHeaders?: string[], // string headers
  limitNumberExport?: number, // Giới hạn số lượng bản ghi tối đa được export vd: EXCEL_EXPORT_LIMITS.FOR_CSV
}


/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Interface RES & PARAMS ----------------------------------------------------------------------------------------------
 * ---------------------------------------------------------------------------------------------------------------------
 * */
/**
 * Xác nhận kết nối token
 * */
export type IParamsGenerateAuthURL = {
  businessId: number,
  stateUri: string,
}


/** Lưu cài đặt xuất excel | Google Sheet */
export type IParamsSaveSettingExportDataColumns = {
  id?: number,
  type: number, // Màn hình
  scopeType: number, // DN | CN
  format: number, // Excel | Google Sheet
  name: string,
  columns: any | IDisplayColumn[], // Push server sẽ encode
  configs?: {
    typeUseFile: number,
    pageSelection: IPageSelectionExportData,
    fileNew: {
      fileName: string,
      sheetName: string,
    },
    // Dùng riêng "Google Sheet"
    googleFile: {
      typeUseSheet: number, // Loại sheet đang dùng
      spreadUrl: string,
      spreadSheetNewTitle: string,
      // 2 thuộc tính lưu chọn giá trị từ sheet đã có sẵn
      spreadSheetId: number,
      spreadSheetTitle: string,
    },
  } | string
}

export type IParamsLoadSettingExportData = {
  type: number, // Màn hình
  scopeType?: number, // DN | CN
  format: number, // Excel | Google Shee
}

/** Params export Google Sheet */
export type IPageSelectionExportData = 'recordAll' // Tất cả các trang
  | 'recordPage' // Trang hiện tại
  | 'recordSelected' // Các dòng đã chọn
  | 'recordGroupPsParent'; // Gộp theo lớp cha (SP cha, đơn hàng không tách dòng theo SP trong đơn)


export type IConfigExportDataGoogleSheet = IExcelDataAbstractState & {
  link: string;
  pageSelection: IPageSelectionExportData, // Chọn kiểu xuất dữ liệu
  settingId?: number, // Mẫu cài đặt đã lưu
  // Dùng chung cả "Excel | Google Sheet"
  typeUseFile: number, // 1 Mới | 2 Đã có
  fileNew: {
    fileName: string,
    sheetName: string,
  },
  // Dùng riêng "Google Sheet"
  googleFile: {
    typeUseSheet: number, // Loại sheet đang dùng
    spreadUrl: string,
    spreadSheetNewTitle: string, // Tên sheet mới
    // 2 thuộc tính lưu chọn giá trị từ sheet đã có sẵn
    spreadSheetId: number,
    spreadSheetTitle: string,
  },
  columns: IDisplayColumn[], // Push server sẽ encode

  // Lặp lại thông tin đơn hàng ở từng dòng sản phẩm nếu đơn hàng hoặc hóa đơn có từ 2 sản phẩm trở lên.
  rowDataLoop?: boolean,
}

// Load sanh sách sheet trong file
export type IParamsLoadSpreadSheets = {
  businessId: number,
  spreadId: string
  accessTokenId: number
}

// Xóa access token
export type IParamsRevokeToken = {
  id: number,
  email: string,
}

// Xóa cài đặt export data
export type IParamsDeleteSettingColumn = {
  businessId: number,
  id: number,
}

